import { logout } from "@/utils/authentication";
import { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { CustomToastComponent } from "@/components/shared";

const SessionContext = createContext<any>([]);

export const SessionProvider = ({ children }: any) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["toasts"]);

  const [tokenExpiration, setTokenExpiration] = useState<number | null>(null);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [loggedInTime, setLoggedInTime] = useState<number | null>(null);

  useEffect(() => {
    // Use token from session to determine if user is logged in
    const token = sessionStorage.getItem("token");
    const storedLoggedInTime = sessionStorage.getItem("loggedIn");

    if (token) {
      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      setTokenExpiration(tokenPayload.exp * 1000); // Convert to milliseconds

      if (storedLoggedInTime) {
        setLoggedInTime(Number(storedLoggedInTime));
        setLastActivity(Date.now());
      }
    } else {
      setTokenExpiration(null);
      setLoggedInTime(null);
    }
  }, [location.pathname]);

  // Update last activity time when user interacts with the page
  useEffect(() => {
    if (loggedInTime) {
      const activityEvents = ["mousemove", "keydown", "scroll"];
      const activityHandler = () => setLastActivity(Date.now());

      activityEvents.forEach((event) => {
        window.addEventListener(event, activityHandler);
      });

      return () => {
        activityEvents.forEach((event) => {
          window.removeEventListener(event, activityHandler);
        });
      };
    }
  }, [loggedInTime]);

  // Check for token expiration
  useEffect(() => {
    if (tokenExpiration) {
      const checkTokenExpiration = setInterval(() => {
        const currentTime = Date.now();
        if (currentTime >= tokenExpiration) {
          sessionLogout("expired"); // Token has expired
          clearInterval(checkTokenExpiration);
        }
      }, 180000); // Check every 3 minutes

      return () => clearInterval(checkTokenExpiration);
    }
  }, [tokenExpiration]);

  // Check for user inactivity
  useEffect(() => {
    if (loggedInTime) {
      const checkActivity = setInterval(() => {
        const elapsed = Date.now() - lastActivity;
        const twoHours = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

        if (elapsed >= twoHours) {
          sessionLogout("inactive"); // User has been inactive
          clearInterval(checkActivity);
        }
      }, 60000); // Check every minute

      return () => clearInterval(checkActivity);
    }
  }, [lastActivity, loggedInTime]);

  const sessionLogout = async (reason: "expired" | "inactive") => {
    toast(CustomToastComponent, {
      toastId: reason === "expired" ? "session-expired" : "session-inactive",
      data: {
        title: t(reason === "expired" ? "toasts:toasts.sessionexpired" : "toasts:toasts.sessioninactive"),
        content: t(reason === "expired" ? "toasts:toasts.sessionexpireddescription" : "toasts:toasts.sessioninactivedescription"),
        type: "warning"
      }
    });
    await logout();
    navigate("/login");
  };

  return (
    <SessionContext value={{}}>
      {children}
    </SessionContext>
  );
};
